/**
 * ChequeLayoutEditor — Batch #14.336 : placer soi-même chaque élément sur le chèque (glisser-déposer + X / Y en mm,
 * taille, activer / désactiver). Aperçu à l'échelle d'un chèque 215,9 × 93,1 mm ; positions = coin supérieur gauche de la case.
 */
import { useEffect, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Btn, inputCls } from "./_shared";

export const CHEQUE_W_MM = 215.9;
export const CHEQUE_H_MM = 93.1;
export const FIELD_DEFAULTS = {
  date: { x_mm: 166.4, y_mm: 19.8, size: 10, enabled: true },
  payee: { x_mm: 29.2, y_mm: 32.5, size: 11, enabled: true },
  amount: { x_mm: 207.0, y_mm: 32.5, size: 11, enabled: true },
  words: { x_mm: 11.4, y_mm: 43.2, size: 9.5, enabled: true, end_x_mm: 189.2 },
  memo: { x_mm: 14.0, y_mm: 70.6, size: 8.5, enabled: true },
  address: { x_mm: 29.2, y_mm: 38.0, size: 8, enabled: false },
};
const FIELDS = [
  ["date", "Date", "2026-06-21"],
  ["payee", "Bénéficiaire", "NOM DU FOURNISSEUR"],
  ["amount", "Montant (aligné à droite)", "***1 234,56"],
  ["words", "Montant en lettres", "Mille deux cent trente-quatre et 56/100 ----------"],
  ["memo", "Note / Pour", "Note : facture 0001"],
  ["address", "Adresse du bénéficiaire (enveloppe à fenêtre)", "123, rue Exemple · Ville (Québec) H0H 0H0"],
];
const PT_TO_MM = 0.3528;

export default function ChequeLayoutEditor({ fields, onChange }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(3);
  const [drag, setDrag] = useState(null);
  const [active, setActive] = useState("payee");
  const F = Object.fromEntries(Object.keys(FIELD_DEFAULTS).map((k) => [k, { ...FIELD_DEFAULTS[k], ...((fields || {})[k] || {}) }]));
  const setField = (k, patch) => onChange({ ...F, [k]: { ...F[k], ...patch } });
  const round = (n) => Math.round(n * 2) / 2;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setScale(el.clientWidth / CHEQUE_W_MM));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const toMm = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / scale, y: (e.clientY - r.top) / scale };
  };
  const onDown = (k) => (e) => {
    e.preventDefault();
    const m = toMm(e);
    setActive(k);
    setDrag({ key: k, dx: m.x - F[k].x_mm, dy: m.y - F[k].y_mm });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag) return;
    const m = toMm(e);
    const x = round(Math.max(0, Math.min(CHEQUE_W_MM, m.x - drag.dx)));
    const y = round(Math.max(0, Math.min(CHEQUE_H_MM, m.y - drag.dy)));
    setField(drag.key, { x_mm: x, y_mm: y });
  };
  const onUp = () => setDrag(null);

  const a = F[active];
  return (
    <div className="space-y-3" data-testid="cheque-layout-editor">
      <div className="text-xs text-slate-500">Glissez chaque élément à l'endroit exact où il doit s'imprimer sur votre chèque, ou saisissez ses coordonnées (mm depuis le coin supérieur gauche du chèque). Le point d'ancrage est le <b>début de la ligne de texte</b> (sa droite pour le montant).</div>
      <div ref={ref} className="relative w-full select-none rounded-md border-2 border-dashed border-slate-300 bg-[#f6f8fb] overflow-hidden" style={{ height: CHEQUE_H_MM * scale }} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} data-testid="cheque-canvas">
        <div className="absolute left-1 top-1 text-[10px] text-slate-400">Chèque 8½ × 3⅔ po — les libellés, la banque et la ligne MICR sont pré-imprimés</div>
        <div className="absolute left-2 right-2 bottom-2 border-t border-slate-300 text-[9px] text-slate-400 pt-0.5 font-mono tracking-widest">⑆000000⑆ 000⑆000 0000000⑈</div>
        {FIELDS.map(([k, label, sample]) => {
          const f = F[k];
          if (!f.enabled) return null;
          const fontPx = Math.max(6, f.size * PT_TO_MM * scale);
          const style = { top: (f.y_mm - f.size * PT_TO_MM) * scale, fontSize: fontPx, lineHeight: 1 };
          if (k === "amount") style.right = (CHEQUE_W_MM - f.x_mm) * scale; else style.left = f.x_mm * scale;
          return (
            <div key={k} onPointerDown={onDown(k)} onClick={() => setActive(k)} title={`${label} — X ${f.x_mm} mm · Y ${f.y_mm} mm`}
                 className={`absolute cursor-move px-1 rounded border whitespace-nowrap ${active === k ? "border-sky-500 bg-sky-50/90 text-sky-900 shadow" : "border-transparent hover:border-slate-300 bg-white/70 text-slate-800"} ${k === "payee" || k === "amount" ? "font-bold" : ""}`}
                 style={style} data-testid={`cheque-field-${k}`}>
              {k === "address" ? sample.split(" · ").map((l) => <div key={l}>{l}</div>) : sample}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="cheque-field-tabs">
        {FIELDS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setActive(k)} className={`px-2 py-1 rounded-full text-xs border ${active === k ? "bg-sky-600 text-white border-sky-600" : F[k].enabled ? "bg-white border-slate-300 text-slate-700 hover:border-slate-400" : "bg-slate-100 border-slate-200 text-slate-400 line-through"}`} data-testid={`cheque-field-tab-${k}`}>{label}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end rounded-lg border border-slate-200 bg-white p-2" data-testid="cheque-field-form">
        <label className="text-xs text-slate-600">X (mm)<input type="number" step="0.5" min="0" max={CHEQUE_W_MM} value={a.x_mm} onChange={(e) => setField(active, { x_mm: Number(e.target.value) })} className={`${inputCls} font-mono`} data-testid="cheque-field-x" /></label>
        <label className="text-xs text-slate-600">Y (mm)<input type="number" step="0.5" min="0" max={CHEQUE_H_MM} value={a.y_mm} onChange={(e) => setField(active, { y_mm: Number(e.target.value) })} className={`${inputCls} font-mono`} data-testid="cheque-field-y" /></label>
        <label className="text-xs text-slate-600">Taille (pt)<input type="number" step="0.5" min="6" max="18" value={a.size} onChange={(e) => setField(active, { size: Number(e.target.value) })} className={`${inputCls} font-mono`} data-testid="cheque-field-size" /></label>
        {active === "words" && <label className="text-xs text-slate-600">Fin des tirets X (mm)<input type="number" step="0.5" min="0" max={CHEQUE_W_MM} value={a.end_x_mm ?? FIELD_DEFAULTS.words.end_x_mm} onChange={(e) => setField(active, { end_x_mm: Number(e.target.value) })} className={`${inputCls} font-mono`} data-testid="cheque-field-end-x" /></label>}
        <label className="text-xs text-slate-600 flex items-center gap-2 pb-2"><input type="checkbox" checked={!!a.enabled} onChange={(e) => setField(active, { enabled: e.target.checked })} data-testid="cheque-field-enabled" /> Imprimer cet élément</label>
        <div className="flex gap-1 justify-end col-span-2 sm:col-span-1">
          <Btn tone="slate" icon={RotateCcw} onClick={() => setField(active, FIELD_DEFAULTS[active])} testId="cheque-field-reset">Cet élément</Btn>
          <Btn tone="slate" onClick={() => onChange({ ...FIELD_DEFAULTS })} testId="cheque-fields-reset-all">Tout réinitialiser</Btn>
        </div>
      </div>
    </div>
  );
}
